<template>
  <div class="col-12" v-if="isAuthenticated && commentable">
    <div class="row">
      <div class="col-12" v-if="uploadstatus">
        <div class="text-center">{{ 'Uploading' | translate }}...</div>
        <div
          class="progress mb-4"
          role="progressbar"
          aria-label="Upload progress"
          :aria-valuenow="uploadstatus"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div class="progress-bar progress-bar-striped progress-bar-animated" :style="`width: ${uploadstatus}%`"></div>
        </div>
      </div>
      <div class="col-12 d-flex" v-else>
        <div class="me-3">
          <Avatar :user="student"></Avatar>
        </div>
        <div class="w-100">
          <div class="mb-2">
            <textarea
              class="form-control comment--input"
              v-model="body"
              :placeholder="$t('Add comment')"
              rows="1"
              @input="autosize"
              @focus="showAdditionalOptions"
              @blur="hideAdditionalOptions"
              ref="autosize"
            ></textarea>
            <div v-if="file" class="ratio ratio-16x9">
              <video :src="fileUrl"></video>
            </div>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <div>
              <div v-if="showOptions && !file">
                <button @click="addVideo" class="btn btn-link text-secondary btn-sm"><i class="far fa-play-circle"></i></button>
              </div>
              <div v-if="file">
                <button @click="removeVideo" class="btn btn-link text-secondary btn-sm"><i class="fas fa-trash"></i></button>
              </div>
            </div>

            <button
              @click.prevent="addComment"
              class="btn btn-primary btn-sm"
              v-show="body.length > 0"
              :disabled="commentNotValid"
            >
              {{ 'Send' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="commentable.comments && commentable.comments.length">
      <Comment
        v-for="comment in commentable.comments"
        :key="comment.id"
        :comment="comment"
        @commentRemoved="removeComment"
      ></Comment>
    </div>
  </div>

  <div class="col-12 text-center" v-else>
    <p class="lead">{{ 'Want to see the comments?' | translate }}</p>
    <router-link :to="`/${$route.params.course}/watch/login`">
      {{ 'Log in now' | translate }}
    </router-link>
  </div>
</template>

<script>
import h from './../helpers'
import _ from 'lodash'
import dayjs from 'dayjs'
import Avatar from '~/player/components/Avatar'
import Comment from '~/player/components/Comment'
import { Upload } from 'tus-js-client'

export default {
  components: {
    Avatar,
    Comment,
  },
  props: ['commentable'],
  data() {
    return {
      body: '',
      showOptions: false,
      file: null,
      uploadstatus: 0,
    }
  },
  methods: {
    initiateTusUpload(videoUpload, comment) {
      this.upload = new Upload(this.file, {
        endpoint: videoUpload.endpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          AuthorizationSignature: videoUpload.AuthorizationSignature,
          AuthorizationExpire: videoUpload.AuthorizationExpire,
          VideoId: videoUpload.VideoId,
          LibraryId: videoUpload.LibraryId,
        },
        metadata: {
          filetype: this.file.type,
          title: videoUpload.title,
        },
        onError: (error) => {
          iziToast.error({
            title: 'Error',
            message: this.$t('Something went wrong'),
            position: 'bottomLeft',
          })
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          this.uploadstatus = Math.ceil((bytesUploaded / bytesTotal) * 100)
        },
        onSuccess: () => {
          this.uploading = false
          this.uploadFinished = true
          this.transcoding = true
          this.uploadstatus = 0
          this.file = null
          // this.fileUrl = null

          this.$set(this.commentable, 'comments', [...this.commentable.comments, comment])

          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        },
      })

      this.upload.findPreviousUploads().then((previousUploads) => {
        if (previousUploads.length) {
          this.upload.resumeFromPreviousUpload(previousUploads[0])
        }

        this.uploading = true
        this.upload.start()
      })
    },
    addVideo() {
      const fileInput = document.createElement('input')
      fileInput.setAttribute('type', 'file')
      fileInput.setAttribute('accept', 'video/*')
      fileInput.click()
      fileInput.addEventListener('change', (e) => {
        this.file = e.target.files[0]
      })
    },
    removeVideo() {
      this.file = null
    },
    showAdditionalOptions() {
      if (this.commentable.type === 'App\\CommunityPost') {
        this.showOptions = true
      }
    },
    hideAdditionalOptions() {
      setTimeout(() => {
        this.showOptions = false
      }, 200)
    },
    time(date) {
      return dayjs(date).fromNow()
    },
    autosize() {
      let el = this.$refs.autosize
      el.style.height = '5px'
      el.style.height = el.scrollHeight + 'px'
    },
    formatDate(date) {
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },
    removeComment(comment) {
      this.$set(this.commentable, 'comments', [...this.commentable.comments.filter((c) => c.id !== comment.id)])
    },
    addComment() {
      return this.$api.comments
        .store({
          id: this.commentable.id,
          type: this.commentable.type,
          body: this.body,
          file: this.file ? 1 : 0,
        })
        .then(({ data }) => {
          this.body = ''
          if (data.videoUpload) {
            this.videoUpload = data.videoUpload
            this.initiateTusUpload(data.videoUpload, data.comment)
          } else {
            this.$set(this.commentable, 'comments', [...this.commentable.comments, data.comment])
          }
        })
        .then(this.autosize)
        .catch(this.handleErrors)
    },
    handleErrors(error) {
      let status = _.get(error, 'response.status')
      if (status === 401) {
        iziToast.info({
          title: this.$t('Log in'),
          message: this.$t('Log in to set progress'),
        })
      } else if (status === 419) {
        iziToast.info({
          title: this.$t('Log in'),
          message: this.$t('Session expired'),
        })
      } else if (status === 422) {
        iziToast.warning({
          title: this.$t('Warning'),
          message:
            this.$t('my.character_limit', { limit: '1200' }) +
            '. ' +
            this.$t('my.your_comment_length', { length: this.body.length }) +
            '. ' +
            this.$t('Try splitting your comment') +
            '.',
          position: 'bottomLeft',
        })
      } else {
        console.error(error)
        iziToast.error({
          title: 'Error',
          message: this.$t('Something went wrong'),
          position: 'bottomLeft',
        })
      }
    },
  },
  computed: {
    commentNotValid() {
      return this.body.length < 2
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated
    },
    student() {
      return this.$store.state.user
    },
    fileUrl() {
      if (!this.file) {
        return null
      }
      return URL.createObjectURL(this.file)
    },
  },
}
</script>

<style>
.comment--input {
  resize: none;
  overflow: hidden;
}
</style>
